import { atom } from "jotai";
import { GetRemittanceItemUseCase, GetRemittanceItemWithIncompleteDeductionsUseCase } from "@/core/usecases/remittance";
import {
  BuildDeductionFetchParamsUseCase,
  BuildStageTabFetchParamsUseCase,
} from "@/core/usecases/deduction";
import {
  currentPageAtom,
  pageLimitAtom,
  remittanceItemAtom,
  sortColumnAtom,
  getDefaultAnalystAtom,
} from "./remittanceItem.atoms";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { RemittanceRepository } from "@/infrastructure/repositories/remittance";
import { ITableSortColumn } from "@/core/interface";
import { searchRemittanceAtom } from "../main/main.atoms";
import {
  filterInfoAtom,
  isFilterAppliedAtom,
  quickFilterInfoAtom,
} from "../deductionFilter";
import { AppConfig } from "@/infrastructure/services/config";
import { GetRemittanceItemCountUseCase } from "@/core/usecases/remittance/GetRemittanceItemCount.useCase";
import { IRemittanceItemRepositoryResponse } from "@/core/repositories/remittance";
import { workspaceSettingsAtom } from "@/store/workspace";
import { Remittance, RemittanceItem, RemittanceItemProps, RemittanceProps } from "@/core/entities/deduction";
import { remittanceAtom } from "../remittance/remittance.atoms";
import { GetRemittanceItemByIdUseCase } from "@/core/usecases/remittance/GetRemittanceItemById.useCase";
import { createLogger } from "@/infrastructure/services/logger";
import { brandMembersAtom, stagesAtom } from "@/store/lookup";
import { ColumnAliasKey } from "@/core/entities/deduction/remittance/ColumnAliasKeyEnum";

//GraphQL client
const client = GraphQlAdapter.getInstance().getClient();
//Remittance repository
const remittanceRepository = new RemittanceRepository(client);
const dateAdapter = new DateAdapter();
const logger = createLogger("remittanceItem.actions");

//Get remittance items
export const getRemittanceItemsAtom = atom(null, async (get, set, refetch?: boolean) => {
  set(remittanceItemAtom, {
    ...get(remittanceItemAtom),
    isLoading: true,
  });

  try {
    const remittanceItemPage = get(currentPageAtom);
    const remittanceItemPageLimit = get(pageLimitAtom);
    const globalSearch = get(searchRemittanceAtom);
    const globalFilterInfo = get(filterInfoAtom);
    const isFilterApplied = get(isFilterAppliedAtom);
    const quickFilterInfo = get(quickFilterInfoAtom);
    const workspaceSettings = get(workspaceSettingsAtom);
    const appConfig = AppConfig.getInstance();
    const defaultCheckDateRangeInMonths =
      workspaceSettings?.defaultFilterCheckDateRange ?? appConfig.DEFAULT_FILTER_CHECK_DATE_RANGE;
    const isNoDefaultDateRange = defaultCheckDateRangeInMonths === -1;
    const sortColumn = get(sortColumnAtom);

    let remittanceItemsResponse: IRemittanceItemRepositoryResponse | null = null;

    if (quickFilterInfo.statusId === "incomplete") {
      remittanceItemsResponse = await new GetRemittanceItemWithIncompleteDeductionsUseCase(remittanceRepository).execute(
        {
          page_number: remittanceItemPage,
          page_size: remittanceItemPageLimit,
          sort_column: sortColumn?.sortColumnKey ?? undefined,
          sort_order: sortColumn?.order ?? undefined,
          column_alias_key: ColumnAliasKey.INCOMPLETE_REMITTANCE_ITEMS,
        },
        refetch
      );
    } else if (quickFilterInfo.statusId === "all") {
      const today = dateAdapter.getCurrentDate().toISOString();
      const shouldSkipDefaultDateForAppliedEmptyCheckDateFilter =
        isFilterApplied &&
        globalFilterInfo.checkDateRange === null &&
        globalFilterInfo.checkFromDate === null &&
        globalFilterInfo.checkToDate === null;
      const shouldOmitDefaultCheckDateRange = isNoDefaultDateRange || shouldSkipDefaultDateForAppliedEmptyCheckDateFilter;
      const defaultCheckFromDate = shouldOmitDefaultCheckDateRange
        ? appConfig.FILTER_DATE_CONFIG.DEFAULT_FROM_DATE
        : dateAdapter.format(dateAdapter.subtract(today, defaultCheckDateRangeInMonths, "months").toISOString(), "YYYY-MM-DD");
      const filterParams = new BuildDeductionFetchParamsUseCase().execute({
        filterInfo: globalFilterInfo,
        statusId: quickFilterInfo.statusId,
        search: globalSearch,
        config: {
          defaultCheckFromDate,
          defaultCheckToDate: appConfig.FILTER_DATE_CONFIG.DEFAULT_TO_DATE,
          isNoDefaultDateRange: shouldOmitDefaultCheckDateRange,
        },
        includeSearchFields: true,
        includeStatusIds: true,
      });
      remittanceItemsResponse = await new GetRemittanceItemUseCase(remittanceRepository).execute(
        {
          page_number: remittanceItemPage,
          page_size: remittanceItemPageLimit,
          sort_column: sortColumn?.sortColumnKey ?? undefined,
          sort_order: sortColumn?.order ?? undefined,
          column_alias_key: ColumnAliasKey.REMITTANCE_ITEMS_ALL,
          ...filterParams,
        },
        refetch
      );
    } else {
      const stages = get(stagesAtom).data;
      const currentQuickFilterTab = stages.find(stage => stage.id === Number(quickFilterInfo.statusId));
      const totalQuickFiltersCount = stages.filter(stage => stage.queryConfig != null).length;
      const defaultAnalyst = get(getDefaultAnalystAtom);

      const stageParams = new BuildStageTabFetchParamsUseCase().execute({
        currentStage: currentQuickFilterTab,
        totalStageTabCount: totalQuickFiltersCount,
        defaultAnalystId: defaultAnalyst ? Number(defaultAnalyst.value) : undefined,
      });

      remittanceItemsResponse = await new GetRemittanceItemUseCase(remittanceRepository).execute(
        stageParams,
        refetch
      );
    }

    // Update the remittance item atom with the remittance items response
    // Add the columns and summary to the remittance item atom
    set(remittanceItemAtom, {
      ...get(remittanceItemAtom),
      remittanceItems: remittanceItemsResponse?.remittanceItems ?? [],
      totalCount: remittanceItemsResponse?.totalCount ?? 0,
      currentPage: remittanceItemPage,
      pageLimit: remittanceItemPageLimit,
      columns: remittanceItemsResponse?.columns ?? [],
      summary: remittanceItemsResponse?.summary ?? null,
      isLoading: false,
      error: null,
    });
  } catch (error: unknown) {
    set(remittanceItemAtom, {
      ...get(remittanceItemAtom),
      remittanceItems: [],
      totalCount: 0,
      isLoading: false,
      error: error as Error,
    });
  }
});

//Update current page
export const setCurrentPageAtom = atom(null, async (get, set, page: number) => {
  set(remittanceItemAtom, {
    ...get(remittanceItemAtom),
    currentPage: page,
  });
});

//Update column sort
export const setSortColumnAtom = atom(null, async (get, set, sortColumn: ITableSortColumn | null) => {
  set(remittanceItemAtom, {
    ...get(remittanceItemAtom),
    sortColumn: sortColumn,
    currentPage: 1,
  });
});

//Get remittance items count
export const getDeductionCountAtom = atom(null, async (get, set, refetch?: boolean) => {
  // getBrandMembersAtom sets isLoading:true synchronously before its await.
  // FilterTabList's lookup effect runs before its count effects, so this guard
  // catches the race; the one-shot loading effect retries after members settle.
  if (get(brandMembersAtom).isLoading) return;

  // Mark count as loading immediately so FilterTabContent waits and doesn't
  // trigger the list fetch prematurely. If brand members aren't ready yet we
  // return early — isLoading stays true, and FilterTabList runs this atom again
  // once on the first brandMembers isLoading true→false transition after mount.
  set(remittanceItemAtom, {
    ...get(remittanceItemAtom),
    deductionCount: {
      data: get(remittanceItemAtom).deductionCount.data,
      isLoading: true,
      error: null,
    },
  });

  const globalSearch = get(searchRemittanceAtom);
  const globalFilterInfo = get(filterInfoAtom);
  const isFilterApplied = get(isFilterAppliedAtom);

  try {
    const workspaceSettings = get(workspaceSettingsAtom);
    const appConfig = AppConfig.getInstance();
    const defaultCheckDateRangeInMonths =
      workspaceSettings?.defaultFilterCheckDateRange ?? appConfig.DEFAULT_FILTER_CHECK_DATE_RANGE;
    const isNoDefaultDateRange = defaultCheckDateRangeInMonths === -1;
    const quickFilterInfo = get(quickFilterInfoAtom);
    const shouldSkipDefaultDateForAppliedEmptyCheckDateFilter =
      quickFilterInfo.statusId === "all" &&
      isFilterApplied &&
      globalFilterInfo.checkDateRange === null &&
      globalFilterInfo.checkFromDate === null &&
      globalFilterInfo.checkToDate === null;
    const shouldOmitDefaultCheckDateRange = isNoDefaultDateRange || shouldSkipDefaultDateForAppliedEmptyCheckDateFilter;

    const today = dateAdapter.getCurrentDate().toISOString();
    const defaultCheckFromDate = shouldOmitDefaultCheckDateRange
      ? appConfig.FILTER_DATE_CONFIG.DEFAULT_FROM_DATE
      : dateAdapter.format(dateAdapter.subtract(today, defaultCheckDateRangeInMonths, "months").toISOString(), "YYYY-MM-DD");
    const filterParams = new BuildDeductionFetchParamsUseCase().execute({
      filterInfo: globalFilterInfo,
      statusId: quickFilterInfo.statusId,
      search: globalSearch,
      config: {
        defaultCheckFromDate,
        defaultCheckToDate: appConfig.FILTER_DATE_CONFIG.DEFAULT_TO_DATE,
        isNoDefaultDateRange: shouldOmitDefaultCheckDateRange,
      },
      includeSearchFields: true,
      includeStatusIds: true,
    });

    const remittanceItemsCountResponse = await new GetRemittanceItemCountUseCase(remittanceRepository).execute(
      filterParams,
      refetch
    );

    set(remittanceItemAtom, {
      ...get(remittanceItemAtom),
      deductionCount: {
        data: remittanceItemsCountResponse.remittanceItemCount,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(remittanceItemAtom, {
      ...get(remittanceItemAtom),
      deductionCount: {
        data: get(remittanceItemAtom).deductionCount.data,
        isLoading: false,
        error: error as Error,
      },
    });
  }
});

// Helper function to convert RemittanceItem to RemittanceItemProps
const remittanceItemToProps = (item: RemittanceItem): RemittanceItemProps => {
  return {
    deductionCount: item.deductionCount,
    invoiceNo: item.invoiceNumber,
    invoiceDate: item.invoiceDate,
    grossAmount: item.grossAmount,
    netAmount: item.netAmount,
    discountAmount: item.discountAmount,
    purchaser: item.purchaser,
    description: item.description,
    isDeduction: item.isDeduction ?? false,
    remittanceId: item.remittanceId,
    remittanceItemId: item.remittanceItemId,
    checkNo: item.checkNo,
    priority: item.priority,
    validity: item.validity,
    stage: item.stage,
    status: item.status,
    adjustmentType: item.adjustmentType,
    adjustmentCategory: item.adjustmentCategory,
    analystUser: item.analystUser,
    approverUser: item.approverUser,
    isIncompleteDeduction: item.isIncompleteDeduction,
    isManuallyUploaded: item.isManuallyUploaded,
    isBackupRequested: item.isBackupRequested,
    canRequestBackup: item.canRequestBackup,
    isDisputed: item.isDisputed,
    deductionDispute: item.deductionDispute,
    returned: item.returned,
    age: item.age,
  };
};

//update remittance item
export const updateRemittanceItemAtom = atom(null, async (get, set, updatedRemittanceItem: RemittanceItem) => {
  const remittanceItems = get(remittanceItemAtom).remittanceItems;
  const remittances = get(remittanceAtom).remittances;

  if (remittances.length > 0) {
    const remittance = remittances.find((eachRemittance: Remittance) => eachRemittance.remittanceId === updatedRemittanceItem.remittanceId);
    if (!remittance) {
      logger.error(`No remittance found to update remittance item with remittance id: ${updatedRemittanceItem.remittanceId}`);
      return;
    }
    const updatedRemittances = remittances.map((eachRemittance: Remittance) => {
      if (eachRemittance.remittanceId === updatedRemittanceItem.remittanceId) {
        const updatedRemittanceItemsProps = eachRemittance.remittanceItems?.map((eachRemittanceItem: RemittanceItem) => {
          if (eachRemittanceItem.remittanceItemId === updatedRemittanceItem.remittanceItemId) {
            return remittanceItemToProps(updatedRemittanceItem);
          }
          return remittanceItemToProps(eachRemittanceItem);
        });

        const remittanceProps: RemittanceProps = {
          remittanceId: eachRemittance.remittanceId,
          checkNo: eachRemittance.checkNumber,
          checkDate: eachRemittance.checkDate,
          checkAmount: eachRemittance.checkAmount,
          purchaser: eachRemittance.purchaser,
          deductedAmount: eachRemittance.deductedAmount,
          isNew: eachRemittance.isNew,
          remittanceItems: updatedRemittanceItemsProps ?? [],
        };

        return new Remittance(remittanceProps);
      }
      return eachRemittance;
    });

    set(remittanceAtom, {
      ...get(remittanceAtom),
      remittances: updatedRemittances,
    });
  } else {
    logger.error("No remittances found to update remittance item");
  }

  if (remittanceItems.length > 0) {
    const updatedRemittanceItems = remittanceItems.map((eachRemittanceItem: RemittanceItem) => {
      if (eachRemittanceItem.remittanceItemId === updatedRemittanceItem.remittanceItemId) {
        return new RemittanceItem(remittanceItemToProps(updatedRemittanceItem));
      }
      return eachRemittanceItem;
    });

    set(remittanceItemAtom, {
      ...get(remittanceItemAtom),
      remittanceItems: updatedRemittanceItems,
    });
  } else {
    logger.error("No remittance items found to update remittance item");
  }
});

// GET REMITTANCE ITEM BY ID
export const getRemittanceItemByIdAtom = atom(null, async (_, __, remittanceItemId: number, refetch: boolean = false): Promise<RemittanceItem> => {
  const remittanceItem = await new GetRemittanceItemByIdUseCase(remittanceRepository).execute({ remittanceItemId: remittanceItemId }, refetch);
  return new RemittanceItem(remittanceItemToProps(remittanceItem.remittanceItemById));
});
